import { plural } from "@single-js/common/util/string";
import _ from "lodash";
import { count } from "util/count";
// Returns the number of detached partition instances for a given partition.
export function _countPartitionDetachedSlaves(partition) {
    return count(partition.partitionInstances, (pi) => pi.role === "detached slave" || pi.role === "detached master");
}
// Returns a boolean stating whether this partition's number of slaves is
// different from its expected number of slaves.
export function _getPartitionHasSlaveMismatch(partition) {
    const slaveCount = count(partition.partitionInstances, (pi) => pi.role === "slave");
    return slaveCount !== partition.expectedNumSlaves;
}
// Returns a boolean stating whether this partition is currently being
// asynchronously replicated but should be synchronously replicated.
export function _getPartitionIsIncorrectlyAsync(partition) {
    // DR Replicas' replication will always be async even if sync
    // replication is desired
    if (partition.drReplica) {
        return false;
    }
    if (partition.syncReplicated) {
        return partition.syncState === "async";
    }
    else {
        return false;
    }
}
export function isPartitionImpacted(partition) {
    return _.some(getPartitionErrors(partition));
}
// We define the impacted state of a partition as one of the following things
// being true about this partition:
// * The partition has a different number of slaves from its expected number of
//   slaves
// * The partition has 1 or more detached partition instances
// * The partition's sync state is async but should be sync (7.0 only)
export function getPartitionErrors(partition) {
    const errors = [];
    if (_getPartitionHasSlaveMismatch(partition)) {
        const slaveCount = count(partition.partitionInstances, (pi) => pi.role === "slave");
        errors.push(`This partition currently has ${slaveCount} ${plural("replica", slaveCount)}, but is expected to have ${partition.expectedNumSlaves} ${plural("replica", partition.expectedNumSlaves)}.`);
    }
    const numDetachedInstances = _countPartitionDetachedSlaves(partition);
    if (numDetachedInstances > 0) {
        errors.push(`This partition currently has ${numDetachedInstances} detached ${plural("replica", numDetachedInstances)}. Healthy partitions typically do not have detached replicas.`);
    }
    if (_getPartitionIsIncorrectlyAsync(partition)) {
        errors.push("This partition's replication state is currently asynchronous but partitions in this database are expected to be synchronously replicated.");
    }
    return errors;
}
export function getPartitionStatusMessage(partitionStatus) {
    switch (partitionStatus) {
        case "degraded_recovering":
            return "This partition is degraded, but is attempting to recover.";
        case "degraded_unrecoverable":
            return "This partition is degraded, manual action must be taken to recover.";
        case "offline_recovering":
            return "This partition is offline, but is attempting to transition back online.";
        case "offline":
            return "This partition is offline.";
        case "unknown":
            return "This partition's current health status is unknown.";
        case "online":
            return "This partition is healthy.";
    }
}
export function getDatabaseStatusMessage(databaseStatus) {
    switch (databaseStatus) {
        case "degraded_recovering":
            return "This database is currently in a Degraded Recovering state because at least one of its partitions is Degraded and is attempting to recover.";
        case "degraded_unrecoverable":
            return "This database is currently in a Degraded Unrecoverable state because at least one of its partitions is Degraded and is unable to recover.";
        case "offline_recovering":
            return "This database is currently in an Offline Recovering state because at least one of its partitions is Offline and is attempting to recover.";
        case "offline":
            return "This database is currently in an Offline state because at least one of its partitions is Offline. If all your partitions are Offline, please check with the cluster admin to make sure you have been granted access to this database.";
        case "unknown":
            return "This database's current health status is unknown.";
        case "online":
            return "This database is currently Online and its member partitions are Healthy.";
    }
}
export function getPartitionName(partition) {
    if (partition.kind === "data") {
        return `${partition.databaseName}:${partition.ordinal}`;
    }
    else {
        return partition.databaseName;
    }
}
export function getPartitionType(partition) {
    if (partition.kind === "data") {
        return "Data Partition";
    }
    else {
        return "Reference Partition";
    }
}
export function getPartitionInstanceName(partitionInstance) {
    if (partitionInstance.ordinal !== undefined) {
        return `${partitionInstance.databaseName}:${partitionInstance.ordinal}`;
    }
    else {
        return partitionInstance.databaseName;
    }
}
export function getPartitionInstancePartitionName(partitionInstance) {
    if (partitionInstance.ordinal !== undefined) {
        return `${partitionInstance.databaseName}:${partitionInstance.ordinal}`;
    }
    else {
        return partitionInstance.databaseName;
    }
}
export function getPartitionInstanceType(pi) {
    if (pi.ordinal === undefined) {
        return "Reference Partition Instance";
    }
    else {
        return "Data Partition Instance";
    }
}
export function getPartitionMasterInstance(partition) {
    const instance = partition.partitionInstances.find((partitionInstance) => partitionInstance.role === "master");
    return instance;
}
export function getPartitionReplicaInstance(partition) {
    if (partition.kind === "reference") {
        return "all-leaf-nodes";
    }
    const instance = partition.partitionInstances.find((partitionInstance) => partitionInstance.role === "slave");
    return instance;
}
